import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#ff4d4f', '#52c41a', '#1890ff', '#722ed1', '#eb2f96', '#13c2c2'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        <p className="tooltip-value">
          Total Sale: {payload[0].value?.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

function CustomerChart(props) {
  const { datas } = props;

  if (!datas || datas.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="customer-chart-container">
      <div className="chart-header">
        <h3>Top Customers by Sales</h3>
        <p className="chart-subtitle">Customer-wise sales performance</p>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={datas}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
            barSize={40}
            barGap={8}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e0e0e0"
              vertical={false}
            />
            <XAxis 
              dataKey="customername" 
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 11, fill: '#666' }}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={(value) => `${value?.toLocaleString()}`}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(136, 132, 216, 0.1)' }} />
            <Legend 
              wrapperStyle={{ paddingTop: 15 }}
              iconType="circle"
              formatter={(value) => <span style={{ color: '#333', fontSize: 12 }}>{value}</span>}
            />
            <Bar 
              dataKey="totalsale" 
              fill="#8884d8"
              radius={[8, 8, 0, 0]}
              animationDuration={1500}
              animationEasing="ease-in-out"
              label={{ 
                position: 'top', 
                formatter: (value) => `${value?.toLocaleString()}`,
                fontSize: 10,
                fill: '#666'
              }}
            >
              {datas.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <style  >{`
        .customer-chart-container {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }
        
        .customer-chart-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }
        
        .chart-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f0f0f0;
        }
        
        .chart-header h3 {
          margin: 0 0 5px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .chart-subtitle {
          margin: 0;
          font-size: 12px;
          color: #999;
        }
        
        .chart-wrapper {
          width: 100%;
          overflow-x: auto;
        }
        
        .chart-empty-state {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
        
        /* Custom Tooltip Styles - need to be global */
        :global(.custom-tooltip) {
          background: rgba(0, 0, 0, 0.85);
          padding: 10px 15px;
          border-radius: 8px;
          color: white;
          min-width: 150px;
        }
        
        :global(.tooltip-label) {
          margin: 0 0 5px 0;
          font-size: 12px;
          font-weight: 600;
          color: #ffd700;
        }
        
        :global(.tooltip-value) {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .customer-chart-container {
            padding: 15px;
          }
          
          .chart-header h3 {
            font-size: 14px;
          }
          
          :global(.recharts-default-legend) {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 15px;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerChart;